// Custom Hook for Sibling Folder Data
import { useState, useEffect, useCallback } from 'react';
import { loadSiblingData, loadMultipleSiblingData, loadF1Data } from '../utils/dataLoader';

/**
 * Custom hook for loading data from sibling folder
 * @param {string|Array} fileNames - Single file name or array of names
 * @param {Object} options - Loading options
 * @returns {Object} - Data, loading state, error state, and refetch function
 */
export const useSiblingData = (fileNames, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (Array.isArray(fileNames)) {
        const result = await loadMultipleSiblingData(fileNames);
        setData(result);
      } else {
        const result = await loadSiblingData(fileNames);
        setData(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to load sibling data');
      console.error('Sibling data loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [fileNames]);

  useEffect(() => {
    if (fileNames) {
      loadData();
    }
  }, [loadData]);

  const refetch = useCallback(() => {
    loadData();
  }, [loadData]);

  return { data, loading, error, refetch };
};

/**
 * Custom hook specifically for F1 data
 * @param {Object} options - Loading options
 * @returns {Object} - F1 data with auto-refresh capability
 */
export const useF1Data = (options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadF1 = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await loadF1Data();
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load F1 data');
      console.error('F1 data loading error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadF1();
  }, [loadF1]);

  const refetch = useCallback(() => {
    loadF1();
  }, [loadF1]);

  return { data, loading, error, refetch };
};
